// =============================================================================
// features/simulator/SimulatorResult.tsx — Resultado da simulação (F2-S06).
//
// Estados:
//   empty    → ilustração + call-to-action
//   loading  → skeleton da área de stats + tabela
//   error    → banner colorido por código (422/409/503/403)
//   success  → Stats row (Bricolage) + Card elev-3 com AmortizationTable
//             + botão "Enviar ao cliente" (F14-S06 / refinado F18-S12)
//
// DS §9.8 Stat, §9.3 Card, §9.6 Alert.
// Parcela mensal em Bricolage 800 text-3xl com --brand-azul (primary da bandeira).
//
// F14-S06: botão "Enviar ao cliente" gated por flag + telefone do lead.
//   - Usa useSendSimulation.
//   - Estados: loading (Enviando…), sucesso (toast verde), erro (banner amigável).
//
// F18-S12: refinamento do gating e mensagens de toast.
//   - Flag desligada → botão oculto (return null), não só desabilitado.
//   - Toast de sucesso: "Simulação enviada via WhatsApp ✓".
//   - Erro META_UNAVAILABLE (502): toast "Template de simulação não configurado."
// =============================================================================

import * as React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Stat } from '../../components/ui/Stat';
import { useToast } from '../../components/ui/Toast';
import type { SimulationResult } from '../../hooks/simulator/types';
import { formatBRL, formatDateOnly, formatRate } from '../../hooks/simulator/types';
import type { SendSimulationError } from '../../hooks/simulator/useSendSimulation';
import { useSendSimulation } from '../../hooks/simulator/useSendSimulation';
import type { SimulationError } from '../../hooks/simulator/useSimulate';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { cn } from '../../lib/cn';
import { useChannels } from '../configuracoes/canais/useChannels';

import { AmortizationTable } from './AmortizationTable';

// ─── Props ────────────────────────────────────────────────────────────────────

interface SimulatorResultProps {
  isPending: boolean;
  result: SimulationResult | undefined | null;
  simulationError: SimulationError | null;
  leadId: string | null;
  /** Telefone E.164 do lead selecionado — necessário para gating do botão de envio. */
  leadPhone: string | null | undefined;
  onReset: () => void;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ResultSkeleton(): React.JSX.Element {
  return (
    <div
      className="flex flex-col gap-4 animate-pulse"
      aria-busy="true"
      aria-label="Calculando simulação…"
    >
      {/* Stats skeleton */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="rounded-md border border-border bg-surface-1 p-5 h-24"
            style={{ boxShadow: 'var(--elev-1)' }}
          >
            <div className="h-2 w-16 rounded bg-surface-2 mb-3" />
            <div className="h-7 w-24 rounded bg-surface-2" />
          </div>
        ))}
      </div>
      {/* Table skeleton */}
      <div
        className="rounded-md border border-border bg-surface-1 h-64"
        style={{ boxShadow: 'var(--elev-2)' }}
      >
        <div className="p-4 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 rounded bg-surface-2 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Estado vazio ─────────────────────────────────────────────────────────────

function EmptyState(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-16 text-center">
      {/* Ilustração minimalista — calculadora com estrela */}
      <div
        className="w-16 h-16 rounded-xl flex items-center justify-center"
        style={{ background: 'var(--bg-elev-2)', boxShadow: 'var(--elev-2)' }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
          <rect
            x="6"
            y="4"
            width="20"
            height="24"
            rx="3"
            stroke="var(--brand-azul)"
            strokeWidth="1.5"
          />
          <rect
            x="10"
            y="8"
            width="12"
            height="5"
            rx="1.5"
            fill="var(--brand-azul)"
            opacity="0.15"
            stroke="var(--brand-azul)"
            strokeWidth="1.2"
          />
          <circle cx="11" cy="18" r="1.5" fill="var(--brand-verde)" />
          <circle cx="16" cy="18" r="1.5" fill="var(--brand-verde)" />
          <circle cx="21" cy="18" r="1.5" fill="var(--brand-verde)" />
          <circle cx="11" cy="23" r="1.5" fill="var(--text-4)" />
          <circle cx="16" cy="23" r="1.5" fill="var(--text-4)" />
          <circle cx="21" cy="23" r="1.5" fill="var(--brand-amarelo)" />
        </svg>
      </div>

      <div className="flex flex-col gap-2 max-w-xs">
        <h3
          className="font-display font-bold text-ink"
          style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.025em' }}
        >
          Pronto para simular
        </h3>
        <p className="font-sans text-sm text-ink-3 leading-relaxed">
          Preencha o formulário ao lado com o lead, produto, valor e prazo, então clique em{' '}
          <strong className="font-semibold text-ink-2">Simular</strong>.
        </p>
      </div>
    </div>
  );
}

// ─── Banner de erro (simulação) ───────────────────────────────────────────────

function ErrorBanner({ error }: { error: SimulationError }): React.JSX.Element {
  // F44-S06: erro sobre a data da 1ª parcela já aparece junto do campo no
  // formulário (SimulatorForm) — aqui só sinalizamos onde olhar, sem repetir
  // a mensagem completa (evita duas fontes de texto para o mesmo erro).
  if (error.code === 'FIRST_DUE_DATE_TOO_SOON' || error.code === 'FIRST_DUE_DATE_TOO_LATE') {
    return (
      <div
        role="alert"
        className="rounded-sm border-l-[3px] p-4"
        style={{
          borderColor: 'var(--warning)',
          background: 'var(--warning-bg)',
        }}
      >
        <p className="font-sans font-semibold text-sm" style={{ color: 'var(--warning)' }}>
          Data da primeira parcela inválida
        </p>
        <p className="font-sans text-xs text-ink-2 mt-1">
          Ajuste o campo <strong className="font-semibold">Data da primeira parcela</strong>, à
          esquerda.
        </p>
      </div>
    );
  }

  if (error.code === 'FLAG_DISABLED' || error.code === 'FORBIDDEN') {
    return (
      <div
        role="alert"
        className="rounded-sm border-l-[3px] p-4"
        style={{
          borderColor: 'var(--danger)',
          background: 'var(--danger-bg)',
        }}
      >
        <p className="font-sans font-semibold text-sm" style={{ color: 'var(--danger)' }}>
          Módulo de simulação desativado
        </p>
        <p className="font-sans text-xs text-ink-2 mt-1">{error.message}</p>
      </div>
    );
  }

  if (error.code === 'NO_RULE_FOR_CITY') {
    return (
      <div
        role="alert"
        className="rounded-sm border-l-[3px] p-4"
        style={{
          borderColor: 'var(--warning)',
          background: 'var(--warning-bg)',
        }}
      >
        <p className="font-sans font-semibold text-sm" style={{ color: 'var(--warning)' }}>
          Sem regra de crédito para esta cidade
        </p>
        <p className="font-sans text-xs text-ink-2 mt-1">
          O produto selecionado não tem regra ativa para a cidade deste lead.{' '}
          <Link
            to="/admin/credit-products"
            className="underline font-medium hover:text-ink"
            style={{ color: 'var(--warning)' }}
          >
            Gerir produtos de crédito
          </Link>
        </p>
      </div>
    );
  }

  if (error.code === 'VALIDATION_ERROR') {
    return (
      <div
        role="alert"
        className="rounded-sm border-l-[3px] p-4"
        style={{
          borderColor: 'var(--danger)',
          background: 'var(--danger-bg)',
        }}
      >
        <p className="font-sans font-semibold text-sm" style={{ color: 'var(--danger)' }}>
          Parâmetros fora dos limites
        </p>
        <p className="font-sans text-xs text-ink-2 mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="rounded-sm border-l-[3px] p-4"
      style={{
        borderColor: 'var(--danger)',
        background: 'var(--danger-bg)',
      }}
    >
      <p className="font-sans font-semibold text-sm" style={{ color: 'var(--danger)' }}>
        Erro ao calcular simulação
      </p>
      <p className="font-sans text-xs text-ink-2 mt-1">{error.message}</p>
    </div>
  );
}

// ─── Banner de erro de envio ──────────────────────────────────────────────────

/**
 * Título curto do banner por código de erro (F50-S08 — cada recusa conhecida
 * tem mensagem própria, nunca um genérico "Envio não autorizado" que confunde
 * quem tinha permissão e telefone certos).
 */
function sendErrorTitle(code: SendSimulationError['code']): string {
  switch (code) {
    case 'META_UNAVAILABLE':
      return 'WhatsApp indisponível';
    case 'NO_PHONE':
      return 'Sem telefone cadastrado';
    case 'TEMPLATE_CATEGORY_NOT_ALLOWED':
      return 'Cliente ainda não escreveu';
    case 'CONTACT_BLOCKED':
      return 'Contato bloqueado';
    case 'SEND_IN_PROGRESS':
      return 'Envio em andamento';
    case 'FLAG_DISABLED':
      return 'Envio desativado';
    case 'FORBIDDEN':
      return 'Envio não autorizado';
    case 'UNKNOWN':
      return 'Não foi possível enviar';
  }
}

function SendErrorBanner({
  error,
  onDismiss,
}: {
  error: SendSimulationError;
  onDismiss: () => void;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-start justify-between gap-3 rounded-sm border-l-[3px] p-4"
      style={{
        borderColor: error.code === 'META_UNAVAILABLE' ? 'var(--warning)' : 'var(--danger)',
        background: error.code === 'META_UNAVAILABLE' ? 'var(--warning-bg)' : 'var(--danger-bg)',
      }}
    >
      <div>
        <p
          className="font-sans font-semibold text-sm"
          style={{
            color: error.code === 'META_UNAVAILABLE' ? 'var(--warning)' : 'var(--danger)',
          }}
        >
          {sendErrorTitle(error.code)}
        </p>
        <p className="font-sans text-xs text-ink-2 mt-1">{error.message}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fechar aviso"
        className="shrink-0 opacity-60 hover:opacity-100 transition-opacity mt-0.5"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          className="w-4 h-4"
          aria-hidden="true"
        >
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  );
}

// ─── Botão de envio ao cliente ────────────────────────────────────────────────

/**
 * Botão "Enviar ao cliente" com gating por flag + telefone + seletor de canal (F20-S07).
 *
 * Gates (F18-S12):
 *   - Flag `simulations.send.enabled` desligada → componente não é renderizado.
 *   - Lead sem telefone → botão desabilitado com tooltip "Lead sem telefone cadastrado".
 *   - Ambos OK → botão habilitado.
 *
 * Canal (F20-S07):
 *   - Se só há 1 canal ou nenhum: exibe "Será enviado por: [nome do canal padrão]" em texto.
 *   - Se há múltiplos canais: mostra seletor inline de canal.
 *   - channelId null = usar canal padrão da org.
 *
 * Toasts:
 *   - Sucesso: "Simulação enviada via WhatsApp ✓"
 *   - already_sent: "Simulação já enviada anteriormente" (info)
 *   - META_UNAVAILABLE (502): "Template de simulação não configurado. Contate o administrador."
 *   - Outros erros: mensagem do erro tipado via banner dismissível.
 */
function SendToClientButton({
  simulationId,
  leadPhone,
}: {
  simulationId: string;
  leadPhone: string | null | undefined;
}): React.JSX.Element | null {
  const { toast } = useToast();
  const { enabled: sendFlagEnabled, isLoading: sendFlagLoading } = useFeatureFlag(
    'simulations.send.enabled',
  );

  // Canal selecionado — null = padrão da org (F20-S07)
  const [selectedChannelId, setSelectedChannelId] = React.useState<string | null>(null);
  const [showChannelSelect, setShowChannelSelect] = React.useState(false);

  const { channels } = useChannels();
  const hasMultipleChannels = channels.length > 1;
  const defaultChannel = channels.find((c) => c.is_default) ?? channels[0];

  const [sendError, setSendError] = React.useState<SendSimulationError | null>(null);

  const { send, isPending } = useSendSimulation({
    onSuccess: (data) => {
      setSendError(null);
      if (data.status === 'already_sent') {
        toast('Simulação já enviada anteriormente', 'info');
      } else {
        toast('Simulação enviada via WhatsApp ✓', 'success');
      }
    },
    onError: (err) => {
      // META_UNAVAILABLE (502) → toast de erro específico (template não configurado)
      if (err.code === 'META_UNAVAILABLE') {
        toast('Template de simulação não configurado. Contate o administrador.', 'danger');
      } else {
        // Demais erros → banner dismissível com mensagem detalhada
        setSendError(err);
      }
    },
  });

  const hasPhone = Boolean(leadPhone);

  // Gate 1: flag desligada (após resolver) → ocultar completamente
  if (!sendFlagLoading && !sendFlagEnabled) {
    return null;
  }

  // Botão disponível quando: flag ligada + lead tem telefone + não carregando a flag
  const canSend = sendFlagEnabled && hasPhone && !sendFlagLoading;

  // Tooltip acessível explicando o motivo de desabilitação
  const disabledReason = sendFlagLoading
    ? undefined
    : !hasPhone
      ? 'Lead sem telefone cadastrado'
      : undefined;

  // Nome do canal que será usado no envio
  const effectiveChannel = selectedChannelId
    ? (channels.find((c) => c.id === selectedChannelId) ?? defaultChannel)
    : defaultChannel;

  const channelLabel = effectiveChannel?.name ?? 'Canal padrão';

  return (
    <div className="flex flex-col gap-2">
      {sendError && <SendErrorBanner error={sendError} onDismiss={() => setSendError(null)} />}

      {/* Indicador de canal (F20-S07) */}
      {canSend && (
        <div className="flex items-center gap-2">
          <span className="font-sans text-xs text-ink-3">
            Será enviado por: <span className="font-medium text-ink-2">{channelLabel}</span>
          </span>
          {hasMultipleChannels && (
            <button
              type="button"
              onClick={() => setShowChannelSelect((v) => !v)}
              className="font-sans text-xs text-azul underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20 rounded-xs"
              aria-expanded={showChannelSelect}
            >
              {showChannelSelect ? 'fechar' : 'trocar'}
            </button>
          )}
        </div>
      )}

      {/* Seletor inline de canal (F20-S07) — só quando múltiplos */}
      {canSend && hasMultipleChannels && showChannelSelect && (
        <select
          value={selectedChannelId ?? ''}
          onChange={(e) => {
            setSelectedChannelId(e.target.value || null);
            setShowChannelSelect(false);
          }}
          className={cn(
            'w-full appearance-none font-sans text-sm font-medium text-ink',
            'bg-surface-1 rounded-sm px-3 py-2 pr-8 border border-border-strong',
            'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
            'transition-[border-color,box-shadow] duration-fast ease',
            'focus:outline-none focus:border-azul',
            'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)]',
          )}
          aria-label="Selecionar canal de envio"
        >
          <option value="">Canal padrão da organização</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_handle ? `${c.name} (${c.display_handle})` : c.name}
            </option>
          ))}
        </select>
      )}

      <Button
        variant="secondary"
        size="sm"
        disabled={!canSend || isPending}
        title={disabledReason}
        aria-label={
          isPending
            ? 'Enviando simulação por WhatsApp…'
            : (disabledReason ?? 'Enviar simulação ao cliente por WhatsApp')
        }
        onClick={() => {
          setSendError(null);
          send(simulationId, selectedChannelId);
        }}
        leftIcon={
          isPending ? (
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              className="w-4 h-4 animate-spin"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="6" strokeOpacity={0.25} />
              <path d="M8 2a6 6 0 0 1 6 6" />
            </svg>
          ) : (
            /* WhatsApp-style send icon */
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M14 2L7 9" />
              <path d="M14 2L9 14l-2-5-5-2 12-5z" />
            </svg>
          )
        }
      >
        {isPending ? 'Enviando…' : 'Enviar ao cliente'}
      </Button>
    </div>
  );
}

// ─── Resultado principal ──────────────────────────────────────────────────────

function ResultSuccess({
  result,
  leadId,
  leadPhone,
  onReset,
}: {
  result: SimulationResult;
  leadId: string | null;
  leadPhone: string | null | undefined;
  onReset: () => void;
}): React.JSX.Element {
  // Stat de destaque: parcela mensal
  // DS: Bricolage + --brand-azul, valor mais largo
  const cardRef = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty('--mx', '-9999px');
    el.style.setProperty('--my', '-9999px');
  }, []);

  return (
    <div className="flex flex-col gap-5">
      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Parcela mensal — stat de destaque */}
        <div
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className={cn(
            'relative overflow-hidden col-span-2 sm:col-span-1',
            'rounded-md border p-5',
            'transition-[transform,box-shadow] duration-[250ms] ease-out',
            'hover:-translate-y-0.5',
            '[--mx:-9999px] [--my:-9999px]',
          )}
          style={{
            background: 'var(--grad-azul)',
            borderColor: 'var(--brand-azul)',
            boxShadow: 'var(--elev-3), var(--glow-azul)',
          }}
        >
          {/* Spotlight */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-md"
            style={{
              background:
                'radial-gradient(300px circle at var(--mx) var(--my), rgba(255,255,255,0.08), transparent 60%)',
            }}
          />
          <div className="relative z-10 flex flex-col gap-2">
            <p
              className="font-sans font-semibold uppercase"
              style={{
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              Parcela mensal
            </p>
            <span
              className="font-display font-extrabold leading-none"
              style={{
                fontSize: 'var(--text-3xl)',
                letterSpacing: '-0.04em',
                fontVariationSettings: "'opsz' 48",
                color: 'white',
                textShadow: '0 2px 12px rgba(0,0,0,0.2)',
              }}
            >
              {formatBRL(result.monthly_payment)}
            </span>
            <p className="font-sans text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
              por {result.term_months} meses
            </p>
          </div>
        </div>

        {/* Total a pagar */}
        <Stat
          label="Total a pagar"
          value={formatBRL(result.total_amount)}
          description={`${result.term_months} parcelas`}
        />

        {/* Total de juros */}
        <Stat
          label="Total de juros"
          value={formatBRL(result.total_interest)}
          trend={{
            value: `${((result.total_interest / result.amount_requested) * 100).toFixed(1)}%`,
            direction: 'neutral',
          }}
        />

        {/* Taxa aplicada */}
        <Stat
          label="Taxa aplicada"
          value={formatRate(result.rate_monthly_snapshot)}
          description="ao mês"
        />
      </div>

      {/* Carência da 1ª parcela — F44-S06.
          first_due_date/grace_days/grace_interest vêm prontos do backend
          (nunca recalculados aqui). Null só ocorre em simulação anterior à
          F44 — exibe "Não informada" em vez de inventar um número. */}
      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-sm border border-border bg-surface-1 px-4 py-3"
        style={{ boxShadow: 'var(--elev-1)' }}
      >
        <span className="font-sans text-xs text-ink-3">
          1ª parcela em{' '}
          <span
            className="font-medium text-ink-2"
            style={{ fontFamily: 'var(--font-mono)', letterSpacing: '-0.01em' }}
          >
            {formatDateOnly(result.first_due_date)}
          </span>
        </span>
        <span className="font-sans text-xs text-ink-3">
          Carência:{' '}
          <span
            className="font-medium text-ink-2"
            style={{ fontFamily: 'var(--font-mono)', letterSpacing: '-0.01em' }}
          >
            {result.grace_days !== null ? `${result.grace_days} dias` : 'Não informada'}
          </span>
        </span>
        <span className="font-sans text-xs text-ink-3">
          Juros da carência:{' '}
          <span
            className="font-medium text-ink-2"
            style={{ fontFamily: 'var(--font-mono)', letterSpacing: '-0.01em' }}
          >
            {result.grace_interest !== null ? formatBRL(result.grace_interest) : 'Não informada'}
          </span>
        </span>
      </div>

      {/* Tabela de amortização */}
      <div>
        <h3
          className="font-display font-bold text-ink mb-3"
          style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
        >
          Tabela de amortização (Price)
        </h3>
        <AmortizationTable rows={result.amortization_table} />
      </div>

      {/* Ações */}
      <div className="flex flex-wrap gap-3 pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          leftIcon={
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              className="w-4 h-4"
            >
              <path d="M2 8a6 6 0 1 1 1.2 3.6" />
              <path d="M2 12V8h4" />
            </svg>
          }
        >
          Nova simulação
        </Button>

        {leadId && (
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="w-4 h-4"
              >
                <path d="M13 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                <path d="M7 10a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
                <path d="M1 15c0-2.8 2.69-5 6-5h6c3.31 0 6 2.2 6 5" />
              </svg>
            }
          >
            <Link to={`/crm/${leadId}`} className="no-underline text-inherit">
              Ver no CRM
            </Link>
          </Button>
        )}

        {/* Botão "Enviar ao cliente" — F14-S06 */}
        <SendToClientButton simulationId={result.id} leadPhone={leadPhone} />
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Painel de resultado da simulação.
 * Gerencia os 4 estados: empty, loading, error, success.
 *
 * F14-S06: aceita `leadPhone` para gating do botão "Enviar ao cliente".
 */
export function SimulatorResult({
  isPending,
  result,
  simulationError,
  leadId,
  leadPhone,
  onReset,
}: SimulatorResultProps): React.JSX.Element {
  if (isPending) {
    return <ResultSkeleton />;
  }

  if (simulationError && !result) {
    return <ErrorBanner error={simulationError} />;
  }

  if (result) {
    return (
      <ResultSuccess result={result} leadId={leadId} leadPhone={leadPhone} onReset={onReset} />
    );
  }

  return <EmptyState />;
}
